<template>
	<div
		:class="[
			'min-h-screen flex flex-col',
			[activeQuestion === 0 ? '' : 'bg-white'],
		]"
	>
		<Progress :active="activeQuestion + 1" :total="slides.length + 1" />
		<template v-if="activeQuestion === 0">
			<Splash
				:heading="splash.heading"
				:subheading="splash.subheading"
				:disabled="Boolean(slides.length)"
				:privacy="splash.privacy"
				v-on:slide-next="addUserName"
				:uiCopy="uiCopy"
			/>
		</template>
		<template v-for="(category, key) in categories">
			<Question
				v-show="slides[activeQuestion] === question.id"
				:activeIndex="
					slides[activeQuestion] === question.id ? activeQuestion : null
				"
				v-for="(question, index) in category.questions"
				:key="question.id"
				:category="key"
				:id="question.id"
				:index="index"
				:text="question.text"
				:title="question.title"
				:answers="question.answers"
				:privacyNote="question.privacyNote"
				:maxScore="question.maxScore"
				:selectionType="question.selectionType"
				v-on:slide-prev="prevHandler"
				v-on:slide-next="nextHandler"
				:uiCopy="uiCopy"
			>
			</Question>
		</template>
		<template v-if="activeQuestion === slides.length - 1"
			><EmailSignup
				:heading="emailSignup.heading"
				:subheading="emailSignup.subheading"
				:privacy="emailSignup.privacy"
				v-on:slide-next="runCalculation"
				:uiCopy="uiCopy"
				:optIn="optIn"
			/>
		</template>
		<template v-if="categories && activeQuestion === slides.length">
			<div class="pt-10 md:pt-18">
				<template v-if="!submitted">
					<div class="max-w-6xl mx-auto md:px-8">
						<h1 class="font-70 type-1000 md:type-1200 px-8 md:px-0 mb-10">
							{{ uiCopy.meetExpert }}
						</h1>
						<FeaturedExpert
							:questions="categories.singleCategory.questions"
							:result="results.singleCategory.answers[0]"
							:userEmail="user.email"
							:handPickCta="results.singleCategory.handPickCta"
							v-on:user-requested="userRequest"
							:uiCopy="uiCopy"
						/>
					</div>
					<div class="bg-primary-300 background-gradient">
						<div
							class="max-w-6xl mx-auto text-white pt-16 md:pt-24 px-8 pb-20"
						>
							<div
								v-if="!results.singleCategory.hideExperts"
								id="additional-experts"
							>
								<h2
									class="font-60 type-1000 mb-16 text-center md:type-1200"
								>
									{{ uiCopy.allOurExperts }}
								</h2>
								<div class="md:flex -mx-6 items-stretch justify-center">
									<ExpertMask
										v-for="n in 3"
										:index="n"
										:questions="
											categories.singleCategory.questions
										"
										:results="results.singleCategory.answers"
										:key="n"
									>
										<Expert
											:questions="
												categories.singleCategory.questions
											"
											:result="results.singleCategory.answers[n]"
											:userEmail="user.email"
											v-on:user-requested="userRequest"
											:uiCopy="uiCopy"
										/>
									</ExpertMask>
								</div>
							</div>
							<div class="max-w-md mx-auto text-center">
								<p class="mb-4 md:mb-6">
									{{ meetYourExpert.privacy }}
								</p>
								<div
									v-if="optIn"
									class="prose-dark type-100 max-w-xs mx-auto"
									v-html="meetYourExpert.withdrawl"
								/>
							</div>
						</div>
					</div>
				</template>
				<template v-else>
					<Submitted :thanks="thanks">{{ uiCopy.close }}</Submitted>
				</template>
			</div>
		</template>
	</div>
</template>

<script>
import { ajaxPost, architectureMatch, compare, formatResults } from './util'
import { fetchIp } from './../js/util'
import { ARCHITECTURE_ID } from './util/common'
import nanoajax from 'nanoajax'

export default {
	data: function () {
		return {
			categories: null,
			activeQuestion: 0,
			questionsLength: null,
			slides: [],
			maxScore: 11,
			optIn: false,
			user: {
				firstName: null,
				lastName: null,
				email: null,
			},
			finalScore: 0,
			submitted: false,
			averageScore: 0,
			results: {
				singleCategory: {
					answers: [],
					hideExperts: false,
					handPickCta: false,
				},
			},
		}
	},
	props: {
		api: {
			type: String,
			required: true,
		},
		allowList: {
			type: Array,
			required: true,
		},
		splash: {
			type: Object,
			required: true,
		},
		emailSignup: {
			type: Object,
			required: true,
		},
		meetYourExpert: {
			type: Object,
			required: true,
		},
		thanks: {
			type: Object,
			required: true,
		},
		uiCopy: {
			type: Object,
			required: true,
		},
		hubspotResults: {
			type: String,
			required: true,
		},
		hubspotMatchRequest: {
			type: String,
			required: true,
		},
	},
	methods: {
		process: function (data) {
			this.categories = {
				singleCategory: { ...data },
			}

			let slidesArray = []

			for (const item of Object.values(this.categories)) {
				for (const question of Object.values(item.questions)) {
					slidesArray.push(question.id)
				}
			}

			this.questionsLength = slidesArray.length
			// add first slide for first/last name
			slidesArray.unshift(null)
			// add last slide for email capture
			slidesArray.push(null)
			this.slides = slidesArray
		},
		addUserName: function (firstName, lastName) {
			this.user.firstName = firstName
			this.user.lastName = lastName
			this.activeQuestion++
		},
		scrollTop: function () {
			window.scrollTo({
				top: 0,
				behavior: 'smooth',
			})
		},
		prevHandler: function () {
			this.activeQuestion--
			this.scrollTop()
		},
		userRequest: function (data) {
			ajaxPost(data, this.hubspotMatchRequest)
			this.submitted = true
		},
		nextHandler: function (data) {
			const { category, index, maxScore, score, selectedAnswers } = data
			// check if maxScore is higher that 0 to avoid returning NaN
			const percentage = maxScore ? Math.floor((score / maxScore) * 100) : 0
			this.categories[category].questions[index].percentage = percentage

			this.categories[category].questions[
				index
			].selectedAnswers = selectedAnswers
			this.categories[category].questions[index].activeScore = score

			this.scrollTop()

			// go to next slide
			this.activeQuestion++
		},
		runCalculation: function (e) {
			let reformatArray = false

			let sortedCategories = this.categories.singleCategory.questions.sort(
				compare
			)

			// if the user only has results from one question
			if (sortedCategories[1].percentage === 0) {
				this.results.singleCategory.hideExperts = true
			}

			// if there is a tie across first and second category
			if (
				sortedCategories[0].percentage === sortedCategories[1].percentage
			) {
				this.results.singleCategory.handPickCta = true

				// do we need to reformat the array?
				reformatArray = architectureMatch(sortedCategories)
			}

			let latest = sortedCategories.reduce((accum, current) => {
				accum.push(current.id)
				return accum
			}, [])

			// if there is a tie across categories, make the architecture category(#7144) first
			if (reformatArray) {
				const index = latest.indexOf(ARCHITECTURE_ID)
				latest.splice(index, 1)
				latest.unshift(ARCHITECTURE_ID)
			}

			this.results.singleCategory.answers = latest

			this.submitResults(e)
		},
		submitResults: function (email) {
			let formattedResults = formatResults(
				this.categories.singleCategory.questions
			)

			this.user.email = email

			const data = {
				fields: [
					{
						name: 'firstname',
						value: this.user.firstName,
					},
					{
						name: 'lastname',
						value: this.user.lastName,
					},
					{
						name: 'email',
						value: email,
					},
					{
						name: 'matchmaker_data',
						value: formattedResults,
					},
				],
			}

			ajaxPost(data, this.hubspotResults)

			this.activeQuestion++
		},
	},
	created: function () {
		nanoajax.ajax({ url: this.api }, (code, responseText) => {
			// error handling
			if (code !== 200) {
				console.warn(`${responseText}`)
				return
			}

			this.process(JSON.parse(responseText))
		})

		const getUserIp = async () => {
			const userDetails = await fetchIp()
			if (userDetails.error) {
				console.error(userDetails.error.info)
				return
			}
			if (this.allowList.includes(userDetails.country_code)) {
				this.optIn = true
			}
			if (
				this.allowList.includes(
					`${userDetails.country_code}-${userDetails.region_code}`
				)
			) {
				this.optIn = true
			}
		}

		getUserIp()
	},
}
</script>
